// src/views/funcionarios/FuncionariosList.jsx
import React, { useState, useEffect, useCallback } from "react";
import funcionariosService from "../../services/funcionariosService";
import condominiosService from "../../services/condominiosService";
import { useAuth } from "../../contexts/AuthContext";
import FuncionarioModal from "./FuncionarioModal";
import DeleteModal from "../../components/ui/DeleteModal";

const PER_PAGE = 10;

const STATS_INIT = { total: 0, ativos: 0, desligados: 0, inativos: 0, cargos: 0, semEmail: 0 };

export default function FuncionariosList() {
  const { condominioAtivo, podeEditar } = useAuth();
  const canEdit = podeEditar("funcionarios");

  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [condominios, setCondominios] = useState([]);
  const [stats, setStats]           = useState(STATS_INIT);
  const [cargos, setCargos]         = useState([]);

  const [idCondo, setIdCondo]               = useState("");
  const [search, setSearch]                 = useState("");
  const [filterStatus, setFilterStatus]     = useState("");
  const [filterCargo, setFilterCargo]       = useState("");
  const [page, setPage]                     = useState(1);

  const [showModal, setShowModal]   = useState(false);
  const [editData, setEditData]     = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [serverError, setServerError] = useState(null);

  // Sincroniza com o condomínio ativo do navbar
  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  useEffect(() => {
    condominiosService.listar({ limit: 100 }).then((r) => setCondominios(r.data)).catch(() => {});
  }, []);

  // Carrega stats reais e cargos quando muda condomínio
  const carregarMetadados = useCallback(async () => {
    if (!idCondo) { setStats(STATS_INIT); setCargos([]); return; }
    try {
      const [s, c] = await Promise.all([
        funcionariosService.stats({ idCondominio: idCondo }),
        funcionariosService.listarCargos({ idCondominio: idCondo }),
      ]);
      setStats(s.data || STATS_INIT);
      setCargos(c.data || []);
    } catch { /* silencioso */ }
  }, [idCondo]);

  const carregar = useCallback(async () => {
    if (!idCondo) { setData([]); setPagination({ total: 0, totalPages: 1 }); return; }
    setLoading(true); setError(null);
    try {
      const res = await funcionariosService.listar({
        idCondominio: idCondo, search, status: filterStatus, cargo: filterCargo,
        page, limit: PER_PAGE,
      });
      setData(res.data); setPagination(res.pagination);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [idCondo, search, filterStatus, filterCargo, page]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { carregarMetadados(); }, [carregarMetadados]);

  const condoNome = condominios.find((c) => String(c.id) === String(idCondo))?.nmFantasia || "";
  const totalPages = pagination.totalPages || 1;

  const handleSave = async (form) => {
    setSaving(true); setServerError(null);
    try {
      const payload = { ...form, idCondominio: idCondo };
      if (form.id) await funcionariosService.atualizar(form.id, payload);
      else         await funcionariosService.criar(payload);
      setShowModal(false);
      await Promise.all([carregar(), carregarMetadados()]);
    } catch (err) {
      setServerError(err.message || "Erro ao salvar funcionário.");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true); setError(null);
    try {
      await funcionariosService.deletar(deleteTarget.id);
      setShowDelete(false); setDeleteTarget(null);
      await Promise.all([carregar(), carregarMetadados()]);
    } catch (err) { setError(err.message); }
    finally { setDeleting(false); }
  };

  const handleToggleStatus = async (f) => {
    if (!canEdit) return;
    try {
      await funcionariosService.alterarStatus(f.id, !f.inativo);
      await Promise.all([carregar(), carregarMetadados()]);
    } catch (err) { setError(err.message); }
  };

  const fmtDt = (dt) => {
    if (!dt) return "-";
    try { return new Date((String(dt || "").split("T")[0]) + "T12:00:00").toLocaleDateString("pt-BR"); }
    catch { return dt; }
  };

  const setFilterFromKpi = (st) => { setFilterStatus(st); setPage(1); };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-person-badge me-2" style={{ color: "var(--primary)", fontSize: 22 }} />
            Funcionários
          </h1>
          <p className="page-subtitle">Gerencie os funcionários do condomínio</p>
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <button
            className="btn btn-primary"
            onClick={() => { setEditData(null); setServerError(null); setShowModal(true); }}
            disabled={!idCondo || !canEdit}
            title={!idCondo ? "Selecione um condomínio" : ""}
          >
            <i className="bi bi-plus-lg" /> Novo Funcionário
          </button>
        </div>
      </div>

      {/* KPIs reais (vindos do backend) e clicáveis para filtrar */}
      <div className="row g-3 mb-4">
        {[
          { icon: "bi-person-badge", cls: "blue",   val: stats.total,      label: "Total",      st: "" },
          { icon: "bi-person-check", cls: "green",  val: stats.ativos,     label: "Ativos",     st: "ativo" },
          { icon: "bi-person-x",     cls: "orange", val: stats.desligados, label: "Desligados", st: "desligado" },
          { icon: "bi-person-slash", cls: "gray",   val: stats.inativos,   label: "Inativos",   st: "inativo" },
        ].map((s) => (
          <div className="col-6 col-md-3" key={s.label}>
            <div
              className="stat-card"
              style={{ cursor: "pointer", boxShadow: filterStatus === s.st ? "0 0 0 2px var(--primary)" : undefined }}
              onClick={() => setFilterFromKpi(s.st)}
              title="Clique para filtrar"
            >
              <div className={`stat-icon ${s.cls}`}><i className={`bi ${s.icon}`} /></div>
              <div>
                <div className="stat-value">{s.val ?? 0}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-3 mb-4">
        {[
          { icon: "bi-briefcase", cls: "blue",  val: stats.cargos,   label: "Cargos distintos" },
          { icon: "bi-envelope-x",cls: "orange",val: stats.semEmail, label: "Sem e-mail" },
        ].map((s) => (
          <div className="col-6 col-md-3" key={s.label}>
            <div className="stat-card">
              <div className={`stat-icon ${s.cls}`}><i className={`bi ${s.icon}`} /></div>
              <div>
                <div className="stat-value">{s.val ?? 0}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header-custom">
          <div className="search-wrapper">
            <i className="bi bi-search" />
            <input
              className="search-input"
              placeholder="Buscar por nome, CPF, cargo, e-mail ou celular..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <select
              className="form-select form-select-sm" style={{ width: "auto", fontSize: 13 }}
              value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            >
              <option value="">Todos</option>
              <option value="ativo">Ativos</option>
              <option value="inativo">Inativos</option>
              <option value="desligado">Desligados</option>
            </select>
            <select
              className="form-select form-select-sm" style={{ width: "auto", fontSize: 13 }}
              value={filterCargo} onChange={(e) => { setFilterCargo(e.target.value); setPage(1); }}
            >
              <option value="">Todos os cargos</option>
              {cargos.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => { carregar(); carregarMetadados(); }}>
              <i className="bi bi-arrow-clockwise" />
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger m-3 mb-0" style={{ fontSize: 13 }}>
            <i className="bi bi-exclamation-triangle-fill me-2" />{error}
            <button type="button" className="btn-close float-end" onClick={() => setError(null)} style={{ fontSize: 10 }} />
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-custom">
            <thead>
              <tr>
                <th>#</th><th>Nome</th><th>Cargo</th><th>CPF</th><th>Contato</th>
                <th>Admissão</th><th>Desligamento</th><th>Status</th>
                <th style={{ width: 140 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-5">
                  <span className="spinner-border spinner-border-sm text-primary me-2" />
                  <span style={{ color: "var(--gray-500)" }}>Carregando...</span>
                </td></tr>
              ) : !idCondo ? (
                <tr><td colSpan={9} className="text-center py-5" style={{ color: "var(--gray-400)" }}>
                  <i className="bi bi-buildings" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                  Selecione um condomínio
                </td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-5" style={{ color: "var(--gray-400)" }}>
                  <i className="bi bi-search" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                  Nenhum funcionário encontrado
                </td></tr>
              ) : data.map((f, idx) => (
                <tr key={f.id}>
                  <td style={{ color: "var(--gray-400)", fontSize: 12 }}>{(page - 1) * PER_PAGE + idx + 1}</td>
                  <td>
                    <div style={{ fontWeight: 600, color: "var(--gray-800)" }}>{f.nmCompleto}</div>
                    <div style={{ fontSize: 11.5, color: "var(--gray-400)" }}>
                      {f.sexo === "Feminino" ? "Feminino" : f.sexo === "Masculino" ? "Masculino" : "—"}
                    </div>
                  </td>
                  <td>
                    {f.cargo ? (
                      <span style={{ background:"var(--bg-highlight)", color: "var(--primary)", padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
                        {f.cargo}
                      </span>
                    ) : <span style={{ color: "var(--gray-400)" }}>—</span>}
                  </td>
                  <td style={{ fontFamily: "monospace", fontSize: 12.5 }}>{f.cpf || "—"}</td>
                  <td style={{ fontSize: 12.5 }}>
                    <div>{f.celular || f.telefone || "—"}</div>
                    {f.email && (
                      <div style={{ fontSize: 11, color: "var(--gray-500)" }}>
                        <i className="bi bi-envelope me-1" />{f.email}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 12.5, color: "var(--gray-500)" }}>{fmtDt(f.dtAdmissao)}</td>
                  <td style={{ fontSize: 12.5, color: "var(--gray-500)" }}>{fmtDt(f.dtDesligamento)}</td>
                  <td>
                    {f.dtDesligamento
                      ? <span style={{ background:"var(--bg-warning-soft)", color: "#854D0E", padding: "3px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 600 }}>● Desligado</span>
                      : f.inativo
                        ? <span className="badge-status badge-inativo"><i className="bi bi-circle-fill" style={{ fontSize: 7 }} />Inativo</span>
                        : <span className="badge-status badge-ativo"><i className="bi bi-circle-fill" style={{ fontSize: 7 }} />Ativo</span>}
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="btn btn-outline-secondary btn-sm btn-icon"
                        title={f.inativo ? "Reativar" : "Inativar"}
                        onClick={() => handleToggleStatus(f)}
                        disabled={!canEdit || !!f.dtDesligamento}
                      >
                        <i className={`bi ${f.inativo ? "bi-toggle-off" : "bi-toggle-on"}`} />
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm btn-icon"
                        title="Editar"
                        onClick={() => { setEditData(f); setServerError(null); setShowModal(true); }}
                        disabled={!canEdit}
                      >
                        <i className="bi bi-pencil" />
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm btn-icon"
                        title="Excluir"
                        onClick={() => { setDeleteTarget(f); setShowDelete(true); }}
                        disabled={!canEdit}
                      >
                        <i className="bi bi-trash3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="pagination-wrapper">
          <span className="pagination-info">
            {pagination.total === 0 ? "Nenhum registro" : `Exibindo ${(page - 1) * PER_PAGE + 1}–${Math.min(page * PER_PAGE, pagination.total)} de ${pagination.total} registros`}
          </span>
          <nav>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage((p) => p - 1)}><i className="bi bi-chevron-left" /></button>
              </li>
              {Array.from({ length: totalPages }, (_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 ? "active" : ""}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setPage((p) => p + 1)}><i className="bi bi-chevron-right" /></button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      <FuncionarioModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        editData={editData}
        saving={saving}
        condominioNome={condoNome}
        serverError={serverError}
        clearServerError={() => setServerError(null)}
      />
      <DeleteModal
        show={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.nmCompleto}
        loading={deleting}
      />
    </div>
  );
}
